<template>
  <div class="group-phase-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$v_p = ω/k | v_g = dω/dk$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>相速度 v_p (波纹移动)</label>
            <span class="val">{{ vp.toFixed(2) }} <small>c</small></span>
          </div>
          <div class="hud-item orange">
            <label>群速度 v_g (能量/包络)</label>
            <span class="val">{{ vg.toFixed(2) }} <small>c</small></span>
          </div>
        </div>

        <svg width="100%" height="240" viewBox="0 0 400 240">
          <defs>
             <filter id="glow-wave"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <linearGradient id="packet-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stop-color="#38bdf8" stop-opacity="0.1" />
                <stop offset="50%" stop-color="#38bdf8" stop-opacity="0.4" />
                <stop offset="100%" stop-color="#38bdf8" stop-opacity="0.1" />
             </linearGradient>
          </defs>

          <!-- 背景坐标轴 -->
          <line x1="0" y1="120" x2="400" y2="120" stroke="#1e293b" stroke-dasharray="4,4" />

          <!-- 包络阴影 (Energy distribution) -->
          <path :d="envelopePath" fill="url(#packet-grad)" stroke="none" />
          
          <!-- 核心波包形 (Superposition) -->
          <path :d="packetPath" fill="none" stroke="#fff" stroke-width="2" filter="url(#glow-wave)" opacity="0.9" />

          <!-- 包络线 (Envelope curve) -->
          <path :d="envelopeCurvePath" fill="none" stroke="#64748b" stroke-width="1.5" stroke-dasharray="5,3" />

          <!-- 追踪动点：相追踪 (Crest tracking) -->
          <g :transform="`translate(${phaseX}, ${phaseY})`">
             <circle r="5" fill="#06b6d4" />
             <text y="-10" text-anchor="middle" fill="#06b6d4" font-size="10" font-weight="900">相点 (Crest)</text>
             <path d="M -8,0 L 8,0 M 0,-8 L 0,8" stroke="#fff" stroke-width="1" />
          </g>

          <!-- 追踪参考：群中心 (Group center) -->
          <g :transform="`translate(${groupX}, 200)`">
             <path d="M 0,-10 L -5,0 L 5,0 Z" fill="#f43f5e" />
             <line x1="0" y1="-10" x2="0" y2="-180" stroke="#f43f5e" stroke-width="1.5" stroke-dasharray="4,2" />
             <text y="15" text-anchor="middle" fill="#f43f5e" font-size="10" font-weight="900">能量中心 (Group)</text>
          </g>
        </svg>

        <div class="interaction-hint">观察波纹如何由包络后部产生并移向前方 (正常色散)</div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                 <span class="label">选择色散环境 (Dispersion Environment)</span>
                 <el-radio-group v-model="dispersion" size="mini">
                    <el-radio-button label="normal">正常 (vg < vp)</el-radio-button>
                    <el-radio-button label="none">无色散 (同步)</el-radio-button>
                    <el-radio-button label="anomalous">反常 (vg > vp)</el-radio-button>
                 </el-radio-group>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>核心揭示：</strong>光在玻璃中传播时，不同频率色光的相速度不同，导致波包整体移动速度（<strong>群速度</strong>）减慢。在“无色散”时两者相同；在“反常色散”下，能量比单一波纹移得更快。关键物理结论：<strong>信息和能量始终以群速度传播</strong>。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GroupPhaseVelocityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      t: 0,
      dispersion: 'normal'
    }
  },
  computed: {
    vp() { return 1.8; }, // Phase velocity (velocity of the individual wave crests)
    vg() {
       const maps = { normal: 0.8, none: 1.8, anomalous: 2.8 };
       return maps[this.dispersion];
    },
    groupX() {
       return (this.t * this.vg) % 500 - 50;
    },
    phaseT() {
       return (this.t * this.vp);
    },
    packetPath() {
       let points = [];
       const k = 0.15;
       const omega = k * this.vp; // Simplified
       for (let x = 0; x <= 400; x += 2) {
          const env = Math.exp(-Math.pow(x - this.groupX, 2) / 3000);
          const y = 120 - 50 * env * Math.sin(k * x - omega * this.t);
          points.push(`${x},${y.toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    },
    envelopeCurvePath() {
       let points = [];
       for (let x = 0; x <= 400; x += 4) {
          const env = Math.exp(-Math.pow(x - this.groupX, 2) / 3000);
          points.push(`${x},${(120 - 52 * env).toFixed(1)}`);
       }
       return "M " + points.join(" L ");
    },
    envelopePath() {
       let points = [];
       for (let x = 0; x <= 400; x += 5) {
          const env = Math.exp(-Math.pow(x - this.groupX, 2) / 3000);
          points.push(`${x},${(120 - 50 * env).toFixed(1)}`);
       }
       for (let x = 400; x >= 0; x -= 5) {
          const env = Math.exp(-Math.pow(x - this.groupX, 2) / 3000);
          points.push(`${x},${(120 + 50 * env).toFixed(1)}`);
       }
       return "M " + points.join(" L ") + " Z";
    },
    phaseX() {
       const k = 0.15;
       const omega = k * this.vp;
       // We want to track a crest, sin(kx - wt) = 1 -> kx - wt = pi/2
       // x = (pi/2 + wt) / k
       let x_base = (Math.PI/2 + omega * this.t) / k;
       const period = (2 * Math.PI) / k;
       // Find the crest closest to group center for best visibility
       let x = x_base;
       while(x > this.groupX + period/2) x -= period;
       while(x < this.groupX - period/2) x += period;
       return x;
    },
    phaseY() {
       const env = Math.exp(-Math.pow(this.phaseX - this.groupX, 2) / 3000);
       return 120 - 50 * env;
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); },
  methods: {
    animate() {
       this.t += 0.5;
       this.animFrame = requestAnimationFrame(this.animate);
    }
  }
}
</script>

<style scoped lang="scss">
.group-phase-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }

.viz-header-compact {
  display: flex; justify-content: space-between; align-items: center; padding: 0 4px;
  .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; }
  .formula-chip { background: #fdf4ff; color: #86198f; border: 1px solid #f5d0fe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; }
}

.simulation-stage {
  flex: 1; min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden;
  .hud-panel {
    position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10;
    .hud-item {
      background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px;
      label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; }
      .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; }
      &.cyan { border-left: 3px solid #06b6d4; }
      &.orange { border-left: 3px solid #f97316; }
    }
  }
}

.interaction-hint { position: absolute; bottom: 8px; width: 100%; text-align: center; color: #334155; font-size: 10px; font-weight: bold; }

.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.flex { display: flex; } .flex-col { flex-direction: column; } .gap-2 { gap: 8px; } .justify-between { justify-content: space-between; } .items-center { align-items: center; }

.discovery-card-compact {
  background: #fdf4ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #fae8ff; display: flex; align-items: center; gap: 10px;
  i { color: #a21caf; font-size: 1.1rem; }
  p { flex: 1; margin: 0; font-size: 12px; color: #86198f; line-height: 1.5; strong { color: #701a75; } }
}
</style>
